import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, X, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileToCompressedDataURL } from "@/lib/image";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  role: "",
  qualifications: "",
  bio: "",
  courses: [],
  expertise: [],
  photo: "",
  social_link: "",
  featured: true,
  order: 0,
};

const inputCls =
  "w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition";

export default function FacultyEditDialog({ open, onOpenChange, member, authHeader, onSaved }) {
  const isEdit = !!member?.id;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(member ? { ...empty, ...member } : empty);
  }, [open, member]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)."); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataURL(file, { maxDim: 800, quality: 0.85 });
      update("photo", dataUrl);
      toast.success("Photo ready. Click Save to persist.");
    } catch {
      toast.error("Could not read the image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        order: Number(form.order) || 0,
        courses: form.courses.filter((c) => c && c.trim()),
        expertise: form.expertise.filter((c) => c && c.trim()),
      };
      if (isEdit) {
        await axios.put(`${API}/faculty/${member.id}`, payload, { headers: authHeader });
        toast.success("Faculty updated");
      } else {
        await axios.post(`${API}/faculty`, payload, { headers: authHeader });
        toast.success("Faculty added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const csvUpdate = (key) => (e) =>
    update(key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto rounded-3xl p-0 border-0 shadow-2xl" data-testid="faculty-edit-dialog">
        <div className="bg-[#002570] text-white p-6 md:p-7">
          <DialogHeader>
            <DialogTitle className="font-rubik text-xl md:text-2xl font-bold">
              {isEdit ? "Edit Faculty Member" : "Add Faculty Member"}
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-white/80 mt-1">
              Photos auto-resize to 800px to keep things fast.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={save} className="p-6 md:p-7 space-y-5 bg-white" data-testid="faculty-edit-form">
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              {form.photo ? (
                <img src={form.photo} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="font-rubik text-xl font-bold text-slate-400">
                  {form.name ? form.name.split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") : "—"}
                </span>
              )}
            </div>
            <div className="flex-1">
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Photo</label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-4 py-2 text-sm font-medium cursor-pointer transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {form.photo ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" onChange={onPickPhoto} data-testid="faculty-photo-input" className="hidden" />
                </label>
                {form.photo && (
                  <button type="button" onClick={() => update("photo", "")} className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-3 py-2">
                    <X className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</label>
              <input data-testid="faculty-name-input" required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Role / Designation</label>
              <input data-testid="faculty-role-input" value={form.role} onChange={(e) => update("role", e.target.value)} placeholder="Senior ACCA Faculty" className={`mt-1.5 ${inputCls}`} />
            </div>
          </div>

          <div>
            <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Qualifications</label>
            <input value={form.qualifications} onChange={(e) => update("qualifications", e.target.value)} placeholder="ACCA, CMA USA, MBA" className={`mt-1.5 ${inputCls}`} />
          </div>

          <div>
            <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Bio</label>
            <textarea rows={4} value={form.bio} onChange={(e) => update("bio", e.target.value)} placeholder="Specialises in financial reporting and audit. 10+ years of mentoring..." className={`mt-1.5 ${inputCls} resize-y min-h-[110px]`} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Courses (comma-separated)</label>
              <input value={(form.courses || []).join(", ")} onChange={csvUpdate("courses")} placeholder="ACCA, CMA USA" className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Expertise tags (comma-separated)</label>
              <input value={(form.expertise || []).join(", ")} onChange={csvUpdate("expertise")} placeholder="Audit, Reporting" className={`mt-1.5 ${inputCls}`} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Profile link (optional)</label>
              <input value={form.social_link} onChange={(e) => update("social_link", e.target.value)} placeholder="LinkedIn / personal site" className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Display Order</label>
              <input type="number" value={form.order} onChange={(e) => update("order", e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
          </div>

          <label className="flex items-center gap-3 select-none cursor-pointer">
            <input type="checkbox" data-testid="faculty-featured-checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} className="w-5 h-5 rounded text-[#002570] focus:ring-[#73c3ff]" />
            <span className="font-inter text-sm font-medium text-slate-700">Show on public Faculty page</span>
          </label>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-full">Cancel</button>
            <button type="submit" disabled={loading} data-testid="faculty-save-btn" className="inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3 font-inter font-medium transition-all">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : isEdit ? "Save Changes" : "Add Faculty"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
